/**
 * ClassificationPicker - Wrapper around generic EntityPicker
 * 
 * Provides classification-specific configuration and maintains backward compatibility
 * with any code that imports ClassificationPickerField or ClassificationPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── Classification-specific configuration ────────────────────────────────────────────
export const CLASSIFICATION_CONFIG = {
  apiEndpoint: "/api/Classification/active",
  entityName: "Classification",
  idField: "id",
  displayFields: {
    primary: "description"
  },
  tableHeaders: ["Description"],
  tableAccessors: ["description"],
  searchFields: [
    { label: "Description", field: "description", index: 0 },
  ],
  placeholder: "--Select classification--"
};

// ── ClassificationPickerSelect wrapper ────────────────────────────────────────────────
/**
 * ClassificationPickerSelect
 * 
 * Usage:
 * <ClassificationPickerSelect
 *   value={selectedClassificationObject}
 *   onChange={(val) => setClassification(val)}
 *   placeholder="All"
 * />
 */
export function ClassificationPickerSelect({
  label,
  classificationId,   // Only ID is required for display
  onSelect,
  onClear,
  placeholder = "--Select classification--",
  disabled = false,
  isRequired = false,
  className = ""
}) {
  return (
    <div className="mb-1 w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      
      <EntityPickerSelect
        config={CLASSIFICATION_CONFIG}
        // We only pass an object with the ID. 
        // EntityPickerSelect will match this ID against its internal list.
        value={classificationId ? { id: classificationId } : null} 
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        onChange={(val) => {
          if (val === null) {
            if (onClear) onClear();
          } else {
            // Returns the full object {id, description} so you can update your state
            if (onSelect) onSelect(val);
          }
        }}
      />
    </div>
  );
}

// ── ClassificationPickerField wrapper ────────────────────────────────────────────────
/**
 * ClassificationPickerField
 * 
 * Usage:
 * <ClassificationPickerField
 *   label="Classification"
 *   classificationId={form.classificationId}
 *   displayValue={form.classificationDescription}
 *   onSelect={(classification) => {
 *     set("classificationId", classification.id);
 *     set("classificationDescription", classification.description);
 *   }}
 *   onClear={() => {
 *     set("classificationId", "");
 *     set("classificationDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function ClassificationPickerField({
  classificationId,           // classification-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={CLASSIFICATION_CONFIG}
      entityId={classificationId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── ClassificationPickerModal wrapper ────────────────────────────────────────────────
/**
 * ClassificationPickerModal
 * 
 * Usage (standalone):
 * <ClassificationPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(classification) => {
 *     set("classificationId", classification.id);
 *     set("classificationDescription", classification.description);
 *   }}
 * />
 */
export function ClassificationPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={CLASSIFICATION_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { ClassificationPickerField as default };